/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { FunctionComponent, VNode } from 'preact';
import { HTMLAttributes, useState } from 'preact/compat';
import { Button, Icon } from '@adobe-commerce/elsie/components';
import { Minus, Add } from '@adobe-commerce/elsie/icons';
import { classes, VComponent } from '@adobe-commerce/elsie/lib';
import { useText } from '@adobe-commerce/elsie/i18n';
import '@/plp/components/Facet/Facet.css';

export interface FacetProps extends HTMLAttributes<HTMLDivElement> {
  numberOfOptionsConfig?: number;
  header: VNode;
  buckets: VNode[];
  last?: boolean;
} 

export const Facet: FunctionComponent<FacetProps> = ({ numberOfOptionsConfig = 5, header, buckets, last }) => {
  const translations = useText({
    showMore: 'Search.Facet.showMore',
    showLess: 'Search.Facet.showLess',
  });
  const numberOfOptionsShown = numberOfOptionsConfig;
  const [showMore, setShowMore] = useState(buckets.length < numberOfOptionsShown);

  const numberOfOptions = showMore ? buckets.length : numberOfOptionsShown;

  return (
    <div className={classes(['product-discovery-facet', ['product-discovery-facet--last', last]])}>
      <VComponent className="product-discovery-facet__header" node={header} />
      {buckets.slice(0, numberOfOptions).map((bucket, index) => {
        return <VComponent key={index} className="product-discovery-facet__bucket" node={bucket} />;
      })}
      {!showMore && buckets.length > numberOfOptionsShown && (
        <Button variant="tertiary" icon={<Icon source={Add} size="24" />} onClick={(): void => setShowMore(!showMore)}>
          {translations.showMore}
        </Button>
      )}
      {showMore && buckets.length > numberOfOptionsShown && (
        <Button
          variant="tertiary"
          icon={<Icon source={Minus} size="24" />}
          onClick={(): void => setShowMore(!showMore)}
        >
          {translations.showLess}
        </Button>
      )}
    </div>
  );
};
